import { handleExternalUrlClick } from '@/utils/platform';

/** Marketing page — used as the final fallback when we can't direct-download. */
const DOWNLOAD_PAGE_URL = 'https://littlebird.ai/download';

/**
 * Direct installer endpoint (backend route in links_router.py). It records
 * attribution and 302s to the CDN-hosted binary.
 */
const DIRECT_DOWNLOAD_BASE = 'https://prod.lilbird.co/download/latest';

export type WebDownloadPlatform = 'mac' | 'windows' | 'other';

/**
 * Best-effort OS detection for the browser build. Electron has reliable
 * `window.systemInfo.platform`, but on web we only have the user agent.
 */
export function getWebDownloadPlatform(): WebDownloadPlatform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const platform = navigator.platform ?? '';
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
  if (/Mac/i.test(platform) || /Mac OS X/i.test(ua)) return 'mac';
  return 'other';
}

export type WebDownloadInfo = {
  label: string;
  platform: WebDownloadPlatform;
  /** Direct installer URL, or null when there's no native build. */
  directUrl: string | null;
  /** Always-safe URL to open if the direct download can't run. */
  fallbackUrl: string;
  /** Suggested filename for the download. */
  filename: string;
};

export function getWebDownloadInfo(): WebDownloadInfo {
  const platform = getWebDownloadPlatform();

  if (platform === 'mac') {
    return {
      label: 'Download for Mac',
      platform,
      // `apple` = Apple Silicon (the browser can't tell apart from Intel).
      directUrl: `${DIRECT_DOWNLOAD_BASE}?arch=apple`,
      fallbackUrl: DOWNLOAD_PAGE_URL,
      filename: 'Littlebird.dmg',
    };
  }
  if (platform === 'windows') {
    return {
      label: 'Download for Windows',
      platform,
      directUrl: `${DIRECT_DOWNLOAD_BASE}?arch=windows`,
      fallbackUrl: DOWNLOAD_PAGE_URL,
      filename: 'Littlebird-Installer.exe',
    };
  }
  return {
    label: 'Download the app',
    platform,
    directUrl: null,
    fallbackUrl: DOWNLOAD_PAGE_URL,
    filename: 'Littlebird',
  };
}

/**
 * Start a normal browser download for the current platform's installer.
 * Must be called synchronously from a click handler so Safari/Firefox
 * don't treat it as a blocked popup.
 */
export function startWebDownload(): void {
  const { directUrl, fallbackUrl, filename } = getWebDownloadInfo();

  if (!directUrl) {
    handleExternalUrlClick(fallbackUrl);
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = directUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}
